/**
 * Free Gift Auto-Add & Cart Synchronizer
 * Handles automatically adding/removing free gift product based on cart state.
 */
(() => {
  if (!window.freeGiftConfig || !window.freeGiftConfig.enabled || !window.freeGiftConfig.variantId) {
    return;
  }

  const { variantId: GIFT_VARIANT_ID, productId: GIFT_PRODUCT_ID } = window.freeGiftConfig;
  let isProcessing = false;
  let syncTimeout = null;

  /**
   * Applies UI adjustments to the free gift item in cart drawer / cart page
   */
  function adjustFreeGiftUI() {
    const giftItems = document.querySelectorAll(
      `.cart-item[data-variant-id="${GIFT_VARIANT_ID}"], .cart-item[data-product-id="${GIFT_PRODUCT_ID}"]`
    );

    giftItems.forEach((itemEl) => {
      itemEl.classList.add('cart-item--free-gift');

      // Disable quantity input controls for free gift (max quantity is 1)
      const qtyInput = itemEl.querySelector('.qty-input__input');
      const plusBtn = itemEl.querySelector('.qty-input__btn[name="plus"]');
      const minusBtn = itemEl.querySelector('.qty-input__btn[name="minus"]');

      if (qtyInput) {
        qtyInput.value = '1';
        qtyInput.setAttribute('readonly', 'true');
        qtyInput.setAttribute('max', '1');
        qtyInput.setAttribute('min', '1');
        qtyInput.style.pointerEvents = 'none';
        qtyInput.style.opacity = '0.85';
      }

      if (plusBtn) {
        plusBtn.setAttribute('disabled', 'true');
        plusBtn.style.display = 'none';
      }

      if (minusBtn) {
        minusBtn.setAttribute('disabled', 'true');
        minusBtn.style.display = 'none';
      }

      // Add Free Gift badge if not already rendered
      const titleEl = itemEl.querySelector('.cart-item__title');
      if (titleEl && !itemEl.querySelector('.free-gift-badge')) {
        const badge = document.createElement('span');
        badge.className = 'free-gift-badge product-label product-label--custom';
        badge.style.cssText = 'display:inline-block; font-size:11px; font-weight:600; padding:2px 8px; border-radius:4px; margin-left:6px; background-color:var(--button-bg-color, #00a7e1); color:#ffffff; vertical-align:middle;';
        badge.textContent = window.freeGiftConfig.badgeText || 'Free Gift';
        titleEl.appendChild(badge);
      }
    });
  }

  /**
   * Refreshes the cart drawer / cart page UI
   */
  function refreshCartUI() {
    // Notify theme cart drawer
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));

    // Trigger cart-items element refresh if present
    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.refresh === 'function') {
      cartItems.refresh();
    } else if (window.location.pathname.includes('/cart')) {
      window.location.reload();
    }

    setTimeout(adjustFreeGiftUI, 400);
  }

  /**
   * Synchronizes free gift state in the cart
   */
  async function syncFreeGift(cartData) {
    if (isProcessing) return;

    try {
      isProcessing = true;

      const cart = cartData || (await (await fetch(`${theme.routes.cart}.js`)).json());
      if (!cart || !Array.isArray(cart.items)) return;

      const giftItem = cart.items.find(
        (item) => Number(item.variant_id) === Number(GIFT_VARIANT_ID) || Number(item.product_id) === Number(GIFT_PRODUCT_ID)
      );

      // Count non-gift items in cart
      const regularItemCount = cart.items.reduce((acc, item) => {
        if (Number(item.variant_id) !== Number(GIFT_VARIANT_ID) && Number(item.product_id) !== Number(GIFT_PRODUCT_ID)) {
          return acc + item.quantity;
        }
        return acc;
      }, 0);

      let cartChanged = false;

      if (regularItemCount > 0) {
        // Case 1: Regular items in cart, but Free Gift is missing -> Auto-add it
        if (!giftItem) {
          const addRes = await fetch(`${theme.routes.cartAdd}.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              items: [
                {
                  id: Number(GIFT_VARIANT_ID),
                  quantity: 1,
                  properties: {
                    _is_free_gift: 'true'
                  }
                }
              ]
            })
          });

          if (addRes.ok) {
            cartChanged = true;
          }
        } else if (giftItem.quantity > 1) {
          // Case 2: Free gift quantity exceeds 1 -> Reset to 1
          const changeRes = await fetch(`${theme.routes.cartChange}.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              id: giftItem.key,
              quantity: 1
            })
          });

          if (changeRes.ok) {
            cartChanged = true;
          }
        }
      } else {
        // Case 3: Cart is empty of regular items, but Free Gift is still in cart -> Auto-remove it
        if (giftItem) {
          const changeRes = await fetch(`${theme.routes.cartChange}.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              id: giftItem.key,
              quantity: 0
            })
          });

          if (changeRes.ok) {
            cartChanged = true;
          }
        }
      }

      if (cartChanged) {
        refreshCartUI();
      } else {
        adjustFreeGiftUI();
      }
    } catch (err) {
      console.warn('[FreeGift] Cart sync error:', err);
    } finally {
      isProcessing = false;
    }
  }

  function queueSync(cartData, delay = 150) {
    if (syncTimeout) clearTimeout(syncTimeout);
    syncTimeout = setTimeout(() => {
      syncFreeGift(cartData);
    }, delay);
  }

  // Intercept native fetch calls for cart operations
  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const response = await originalFetch.apply(this, args);
    try {
      const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
      if (
        !isProcessing &&
        (url.includes('/cart/add') ||
          url.includes('/cart/change') ||
          url.includes('/cart/update') ||
          url.includes('/cart/clear'))
      ) {
        // Clone response to inspect if needed or trigger debounced sync
        queueSync(null, 200);
      }
    } catch (e) {
      // ignore
    }
    return response;
  };

  // Watch for DOM mutations in cart drawer / cart page to re-apply UI lock
  const observer = new MutationObserver(() => {
    adjustFreeGiftUI();
  });

  // Initial execution on DOM load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      queueSync(null, 100);
      const cartDrawer = document.getElementById('cart-drawer');
      if (cartDrawer) observer.observe(cartDrawer, { childList: true, subtree: true });
      const cartItemsEl = document.getElementById('cart-items');
      if (cartItemsEl) observer.observe(cartItemsEl, { childList: true, subtree: true });
    });
  } else {
    queueSync(null, 100);
    const cartDrawer = document.getElementById('cart-drawer');
    if (cartDrawer) observer.observe(cartDrawer, { childList: true, subtree: true });
    const cartItemsEl = document.getElementById('cart-items');
    if (cartItemsEl) observer.observe(cartItemsEl, { childList: true, subtree: true });
  }

  // Listen for custom theme cart events
  document.addEventListener('dispatch:cart-drawer:refresh', () => {
    setTimeout(adjustFreeGiftUI, 200);
  });
})();
